<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Message from 'primevue/message'
import CustomerForm from '@/views/customers/CustomerForm.vue'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { useAuthStore } from '@/stores/auth'
import { customerApi, type CustomerPayload } from '@/services/customers'

/**
 * Alta de cliente.
 *
 * `document` no es obligatorio: se envía como `null` cuando el campo queda
 * vacío, para que el backend guarde "sin identificación fiscal" en vez de una
 * cadena vacía que luego ocuparía el índice único.
 */

const router = useRouter()
const authStore = useAuthStore()

const allowed = computed(() => authStore.hasPermission('customers.manage'))
const loading = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const generalError = ref<string | null>(null)

/** Campos del 422, tal y como los devuelve el envelope de errores del proyecto. */
/**
 * Volver al listado **con los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir esta vista se los lleva en la query. Sin devolvérselos, quien filtra, abre una
 * ficha y vuelve se encuentra la lista entera: el filtro se aplicó y desapareció.
 *
 * «Volver» empuja una entrada **nueva**, así que el historial no salva: la entrada anterior,
 * la que tenía los filtros, deja de ser la que se está usando.
 */
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('customers.index')

function extractServerErrors(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as {
    validationErrors?: Record<string, string[]>
    response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } }
  }
  return maybeAxios.validationErrors ?? maybeAxios.response?.data?.error?.details?.fields ?? {}
}

function extractMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo guardar el cliente. Inténtalo de nuevo.'
}

async function handleSubmit(payload: CustomerPayload): Promise<void> {
  loading.value = true
  serverErrors.value = {}
  generalError.value = null

  try {
    await customerApi.create(payload)
    await router.push(destinoListado.value)
  } catch (cause: unknown) {
    serverErrors.value = extractServerErrors(cause)
    // Un 422 ya se explica campo a campo; un aviso global taparía el error real.
    generalError.value = Object.keys(serverErrors.value).length > 0 ? null : extractMessage(cause)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Nuevo cliente
        </h1>
        <p class="mt-1 text-surface-500 dark:text-surface-400">
          El documento es opcional, pero debe ser único si lo rellenas
        </p>
      </div>
      <Button
        label="Volver"
        icon="pi pi-arrow-left"
        severity="secondary"
        @click="volverAlListado"
      />
    </header>

    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      No tienes permiso de gestión de clientes, así que no puedes crear registros.
    </Message>

    <Message
      v-if="generalError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ generalError }}
    </Message>

    <CustomerForm
      v-if="allowed"
      submit-label="Crear"
      :loading="loading"
      :server-errors="serverErrors"
      @submit="handleSubmit"
      @cancel="volverAlListado"
    />
  </div>
</template>
